<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useNavigationPermissions } from '@/composables/permissions/useNavigationPermissions'
import UsersIcon from '@/assets/img/svg/icons/users_outline.svg?component'
import VirtualAgentIcon from '@/assets/img/svg/icons/virtual-agent.svg?component'
import FileIcon from '@/assets/img/svg/icons/file.svg?component'

const { t } = useI18n()
const route = useRoute()
const { hasPermission } = useNavigationPermissions()

interface QuickAction {
  id: string
  titleKey: string
  subtitleKey: string
  path: string
  permissionKey: string
  icon: string
  queryParams?: Record<string, string>
}

const allQuickActions: QuickAction[] = [
  // Supervisor tiles
  {
    id: 'live-supervision',
    titleKey: 'home.quick_actions.live_supervision',
    subtitleKey: 'home.quick_actions.live_supervision_desc',
    path: 'live-supervision',
    permissionKey: 'home:action-supervise-live',
    icon: 'audio-on',
  },
  {
    id: 'cold-monitoring',
    titleKey: 'home.quick_actions.cold_monitoring',
    subtitleKey: 'home.quick_actions.cold_monitoring_desc',
    path: 'cold-monitoring',
    permissionKey: 'home:action-cold-monitoring',
    icon: 'graph',
  },
  // Admin-only tiles
  {
    id: 'users',
    titleKey: 'home.quick_actions.users',
    subtitleKey: 'home.quick_actions.users_desc',
    path: 'users',
    permissionKey: 'home:action-manage-users',
    icon: 'users',
  },
  {
    id: 'virtual-agents',
    titleKey: 'home.quick_actions.virtual_agents',
    subtitleKey: 'home.quick_actions.virtual_agents_desc',
    path: 'configurator',
    permissionKey: 'home:action-configure-agents',
    icon: 'virtual-agents',
  },
  {
    id: 'knowledge',
    titleKey: 'home.quick_actions.knowledge',
    subtitleKey: 'home.quick_actions.knowledge_desc',
    path: 'knowledge',
    permissionKey: 'home:action-manage-knowledge',
    icon: 'file',
  },
  {
    id: 'integration',
    titleKey: 'home.quick_actions.integration',
    subtitleKey: 'home.quick_actions.integration_desc',
    path: 'integration',
    permissionKey: 'home:action-manage-integration',
    icon: 'settings',
  },
]

const visibleQuickActions = computed(() => {
  const locale = route.params.locale || 'en'
  return allQuickActions
    .filter((action) => hasPermission(action.permissionKey))
    .map((action) => ({
      ...action,
      fullPath: `/${String(locale)}/${action.path}`,
    }))
})
</script>

<template>
  <nav
    v-if="visibleQuickActions.length > 0"
    :aria-label="t('home.quick_actions.title')"
    class="space-y-4"
  >
    <h2 class="typography-heading-2 text-neutral-1">{{ t('home.quick_actions.title') }}</h2>

    <div class="grid gap-4 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
      <router-link
        v-for="action in visibleQuickActions"
        :key="action.id"
        :to="{ path: action.fullPath, query: action.queryParams }"
        :data-testid="`quick-action-${action.id}`"
        class="group radius-m focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      >
        <div
          class="flex items-center gap-3 py-6 px-4 radius-m border border-primary-interactive bg-white group-hover:ring-1 group-hover:ring-primary group-hover:bg-[#F3F4FC] transition-all cursor-pointer"
        >
          <div class="p-2 bg-secondary-notice group-hover:bg-[#E4E7FF] radius-m transition-colors">
            <UsersIcon v-if="action.icon === 'users'" class="w-6 h-6 text-primary-interactive" />
            <VirtualAgentIcon
              v-else-if="action.icon === 'virtual-agents'"
              class="w-6 h-6 text-primary-interactive"
            />
            <FileIcon v-else-if="action.icon === 'file'" class="w-6 h-6 text-primary-interactive" />
            <UiIcon
              v-else
              size="xl2"
              :name="action.icon"
              variant="outline"
              class="text-primary-interactive"
            />
          </div>

          <div class="flex flex-col space-y-xs min-w-0">
            <OOverflowTooltip
              :content="t(action.titleKey)"
              as="div"
              class="typography-body-1-medium text-neutral-1"
            >
              <span class="truncate">
                {{ t(action.titleKey) }}
              </span>
            </OOverflowTooltip>
            <OOverflowTooltip
              :content="t(action.subtitleKey)"
              as="div"
              class="typography-body-2 text-neutral-2"
            >
              <span class="truncate">
                {{ t(action.subtitleKey) }}
              </span>
            </OOverflowTooltip>
          </div>
        </div>
      </router-link>
    </div>
  </nav>
</template>
